/**
 * FP3-049 — Workspace Info page.
 *
 *   /account/workspace
 *
 * Calm, scoped surface accessible from the sidebar gear menu where
 * any signed-in user can confirm:
 *   • their organization (name + account type + access type)
 *   • the campuses / schools they're assigned to
 *   • their provider type / staff role (if linked to a provider)
 *   • their permission role
 *   • their supervising provider (assistants only)
 *   • their organization's admin contacts
 *
 * All data comes from a single auth-scoped endpoint:
 *   GET /api/account/workspace-info
 *
 * Org/role/permission boundaries are enforced server-side — a
 * provider's payload contains only their own org, only their own
 * schools, and only the admin contacts inside their own org.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Buildings,
  GraduationCap,
  IdentificationBadge,
  HandHeart,
  ShieldCheck,
  EnvelopeSimple,
  ArrowLeft,
  UsersThree,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
// FP3-059 — In-app assistant access request modal (legacy; kept
// only so we can still render any in-flight pending request a solo
// provider had open before the FP3-069 direct-invite cutover).
import AssistantAccessRequestModal from "@/components/account/AssistantAccessRequestModal";
// FP3-069 — Direct assistant invite modal (replaces the request
// flow above for new invites).
import InviteAssistantModal from "@/components/account/InviteAssistantModal";
// FP3-051 — Solo provider / org-admin schools management card.
import ManageSchoolsCard from "@/components/account/ManageSchoolsCard";
// FP3-062 follow-up — Solo provider self-set provider type.
import ProviderRoleEditor from "@/components/account/ProviderRoleEditor";
// FP3-075 — Account preferences (Simple/Advanced + Fidelity On/Off).
import PreferencesCard from "@/components/account/PreferencesCard";
import { useAuth } from "@/lib/auth";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  org_admin: "Organization Admin",
  admin: "Admin",
  provider: "Provider",
  assistant: "Assistant",
};

const ACCOUNT_TYPE_LABEL = {
  school_team: "School / team",
  individual_provider: "Solo provider",
  district: "District",
};

const ACCESS_TYPE_LABEL = {
  pilot: "Pilot",
  trial: "Trial",
  membership: "Membership",
  active: "Active",
  inherited: "Inherited",
};

function statusTone(s) {
  const value = (s || "").toLowerCase();
  if (value === "active" || value === "accepted") {
    return "bg-[#E8F0EB] text-[#1E3F33]";
  }
  if (value === "pending" || value === "invited") {
    return "bg-[#F5EFE3] text-[#7A6A2C]";
  }
  if (value === "disabled" || value === "inactive") {
    return "bg-[#F8EFEA] text-[#7A3A2C]";
  }
  return "bg-[#F1F2F4] text-[#3F4756]";
}

export default function WorkspaceInfo() {
  const navigate = useNavigate();
  const { isSoloWorkspaceOwner, isSupervisedAssistant } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.getWorkspaceInfo();
        if (alive) setData(r.data || null);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load workspace info.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5EF]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10">
          <div
            data-testid="workspace-info-loading"
            className="text-[13px] text-[#6B7280]"
          >
            Loading workspace info…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F5EF]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 space-y-4">
          <div
            data-testid="workspace-info-error"
            className="text-[12.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2"
          >
            {error}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="workspace-info-error-back-to-today"
              onClick={() => navigate("/today")}
              variant="outline"
              className="h-9 px-3.5 text-[12.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
            >
              ← Back to Today
            </Button>
            <Button
              type="button"
              data-testid="workspace-info-error-retry"
              onClick={() => window.location.reload()}
              className="h-9 px-3.5 text-[12.5px]"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    // Defensive: if data somehow ended up null without an error
    // string we still keep the page navigable instead of blanking
    // out completely.
    return (
      <div className="min-h-screen bg-[#F7F5EF]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 space-y-4">
          <div
            data-testid="workspace-info-empty"
            className="text-[12.5px] text-[#6B7280]"
          >
            Workspace info is not available yet.
          </div>
          <Button
            type="button"
            data-testid="workspace-info-empty-back-to-today"
            onClick={() => navigate("/today")}
            variant="outline"
            className="h-9 px-3.5 text-[12.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            ← Back to Today
          </Button>
        </div>
      </div>
    );
  }

  // FP3-053 — Defensive shape. The backend always returns the full
  // payload, but we never want this page to blank out if a single
  // sub-key is missing (e.g. truncated response, mid-deploy schema
  // skew). Fall back to safe defaults for every consumed field.
  const safeData = data || {};
  const user = safeData.user || {};
  const organization = safeData.organization || null;
  const schools = Array.isArray(safeData.schools) ? safeData.schools : [];
  const provider = safeData.provider || null;
  const supervising = safeData.supervising_provider || null;
  // FP3-090 — plural-aware: render every authorized supervisor.
  const supervisingList = (Array.isArray(safeData.supervising_providers) &&
    safeData.supervising_providers.length > 0)
    ? safeData.supervising_providers
    : (supervising ? [supervising] : []);
  const admins = Array.isArray(safeData.admins) ? safeData.admins : [];
  // FP3-052 — Assistant add-on request summary for solo orgs.
  // Null for non-solo workspaces.
  const assistantRequest = safeData.assistant_request || null;
  // FP3-091 — canonical supervised-assistant relationship list.
  // Populated for any primary/solo provider with rostered, invited,
  // or active assistants assigned to them (activation NOT required).
  const supervisedAssistants = Array.isArray(safeData.supervised_assistants)
    ? safeData.supervised_assistants
    : [];
  const roleLabel = ROLE_LABEL[user.role] || user.role || "—";
  // FP3-051 / FP3-062 / FP3-069 / FP3-079D — Solo provider OWNER
  // capability flag. Owners can manage schools, invite assistants,
  // and edit workspace settings. Only assistants under an ACTIVE
  // supervision relationship are restricted; a solo SLPA/COTA/PTA
  // owner (or one with a stale/nameless supervising pointer) keeps
  // owner controls. Both flags come from the shared auth-context
  // rule (see auth.jsx) so Caseload / Service Demographics /
  // Workspace stay in lockstep.
  const isAssistant = isSupervisedAssistant;
  const isSoloOwner = isSoloWorkspaceOwner;
  const isAdminFamily = ["admin", "org_admin", "super_admin"].includes(
    (user.role || "").toLowerCase()
  );
  const canManageSchools = isSoloOwner || isAdminFamily;

  // Local-edit state for the Schools card (kept lightweight — we
  // re-fetch workspace info after every successful write so the
  // server stays the source of truth).
  const refresh = () => {
    api
      .getWorkspaceInfo()
      .then((r) => setData(r.data || null))
      .catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] body-font">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-8 sm:py-10">
        <button
          type="button"
          data-testid="workspace-info-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[12px] text-[#6B7280] hover:text-[#1E3F33] mb-4 transition-colors"
        >
          <ArrowLeft size={13} weight="bold" />
          Back
        </button>

        <header className="mb-7">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] font-semibold mb-1.5">
            Account · Workspace info
          </div>
          <h1 className="text-[24px] sm:text-[28px] font-semibold text-[#1F2937] heading-font leading-tight">
            Your workspace
          </h1>
          <p className="mt-2 text-[13px] text-[#4B5563] leading-relaxed max-w-2xl">
            A read-only summary of where you live in Frame Practice — your
            organization, campuses, role, and the admins to reach when you
            need help.
          </p>
        </header>

        <div
          data-testid="workspace-info"
          className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
        >
          {/* FP3-075 — Preferences card spans both columns. Surfaced
              first so users see "you can tune this" before the
              read-only org/role detail below. */}
          <PreferencesCard />

          {/* Organization */}
          <SectionCard
            testId="workspace-info-organization"
            icon={<Buildings size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Organization"
            empty={!organization}
            emptyText="You're not currently attached to an organization."
          >
            {organization && (
              <dl className="space-y-2.5">
                <Row label="Name" value={organization.name || "—"} />
                <Row
                  label="Account type"
                  value={
                    ACCOUNT_TYPE_LABEL[organization.account_type] ||
                    organization.account_type ||
                    "—"
                  }
                />
                <Row
                  label="Access type"
                  value={
                    ACCESS_TYPE_LABEL[organization.access_type] ||
                    organization.access_type ||
                    "—"
                  }
                />
                <Row
                  label="Status"
                  value={
                    <span
                      data-testid="workspace-org-status"
                      className={`inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] ${statusTone(organization.status)}`}
                    >
                      {organization.status || "active"}
                    </span>
                  }
                />
              </dl>
            )}
          </SectionCard>

          {/* You */}
          <SectionCard
            testId="workspace-info-you"
            icon={<IdentificationBadge size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="You"
          >
            <dl className="space-y-2.5">
              <Row label="Name" value={user.name || "—"} />
              <Row label="Email" value={user.email || "—"} mono />
              <Row label="Role" value={roleLabel} />
              {provider && (
                <>
                  <Row
                    label="Provider type"
                    value={
                      <ProviderRoleEditor
                        provider={provider}
                        canEdit={isSoloOwner || isAdminFamily}
                        onChanged={refresh}
                      />
                    }
                  />
                  {provider.staff_role && (
                    <Row label="Staff role" value={provider.staff_role} />
                  )}
                  <Row
                    label="Provider status"
                    value={
                      <span
                        className={`inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] ${statusTone(provider.status)}`}
                      >
                        {provider.status || "active"}
                      </span>
                    }
                  />
                </>
              )}
            </dl>
          </SectionCard>

          {/* Campuses / schools */}
          <SectionCard
            testId="workspace-info-schools"
            icon={<GraduationCap size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Campuses & schools"
            empty={!canManageSchools && schools.length === 0}
            emptyText="No schools or workspaces added yet."
            wide
          >
            {canManageSchools ? (
              <ManageSchoolsCard
                organizationId={organization?.id || ""}
                schools={schools}
                onChange={refresh}
              />
            ) : (
              schools.length > 0 && (
                <ul className="space-y-2">
                  {schools.map((s) => (
                    <li
                      key={s.id}
                      data-testid={`workspace-school-${s.id}`}
                      className="flex items-center justify-between gap-3 py-1.5 border-b border-[#F1F2F4] last:border-0"
                    >
                      <div className="min-w-0">
                        <div className="text-[13px] text-[#1F2937] font-medium truncate">
                          {s.name || "Unnamed campus"}
                        </div>
                        {s.campus_type && (
                          <div className="text-[10.5px] text-[#6B7280] uppercase tracking-[0.08em]">
                            {s.campus_type}
                          </div>
                        )}
                      </div>
                      {s.access_status && (
                        <span
                          className={`flex-shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] ${statusTone(s.access_status)}`}
                        >
                          {s.access_status}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}
          </SectionCard>

          {/* Supervising provider (assistants only). FP3-069 follow-up —
              also surface the workspace name + access type so the
              assistant clearly understands whose workspace they
              belong to. */}
          {/* FP3-079D — Hide the ghost card (stale, nameless
              supervising pointer on a solo OWNER); real assistants
              (active supervision) and named supervisors still show. */}
          {supervisingList.length > 0 &&
            (isAssistant || supervisingList.some((s) => (s.name || "").trim())) && (
            <SectionCard
              testId="workspace-info-supervising"
              icon={<HandHeart size={18} weight="duotone" className="text-[#1E3F33]" />}
              title={supervisingList.length > 1 ? "Supervising providers" : "Supervising provider"}
              wide
            >
              <dl className="space-y-2.5">
                {supervisingList.map((sup, idx) => (
                  <React.Fragment key={sup.id || idx}>
                    {idx > 0 && <div className="border-t border-[#EFEDE6] pt-2.5" />}
                    <Row label="Name" value={sup.name || "—"} />
                    <Row
                      label="Provider type"
                      value={sup.role || sup.provider_type || "—"}
                    />
                    <Row
                      label="Status"
                      value={
                        <span
                          className={`inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] ${statusTone(sup.status)}`}
                        >
                          {sup.status || "active"}
                        </span>
                      }
                    />
                  </React.Fragment>
                ))}
                {organization?.name && (
                  <Row
                    label="Workspace"
                    value={
                      <span data-testid="supervising-workspace-name">
                        {organization.name}
                        {organization.access_type && (
                          <span className="text-[11px] text-[#6B7280] ml-1.5">
                            · {ACCESS_TYPE_LABEL[organization.access_type] || organization.access_type}
                          </span>
                        )}
                      </span>
                    }
                  />
                )}
              </dl>
              {isAssistant && (
                <p
                  data-testid="assistant-supervisor-note"
                  className="text-[11px] text-[#6B7280] mt-3 leading-relaxed"
                >
                  {/* FP3-091 — wording works for one OR many supervisors
                      and for both solo and school/team workspaces. */}
                  These providers are authorized to supervise your work.
                  The supervising provider responsible for a specific
                  student is identified on that student&rsquo;s team
                  assignment. School calendar, grade rules, and
                  scheduling context are inherited from this workspace so
                  your students get the same constraints applied
                  automatically.
                </p>
              )}
            </SectionCard>
          )}

          {/* FP3-091 — "Assistants you supervise" relationship card.
              Visible to any primary/solo provider with at least one
              supervised assistant — rostered, invited, or active.
              Read-only for school/team providers; solo owners keep
              their invite controls on the Assistant providers card
              below. Never shows students or caseload details. */}
          {!isAssistant && supervisedAssistants.length > 0 && (
            <SupervisedAssistantsCard
              assistants={supervisedAssistants}
              selfProviderId={provider?.id || ""}
            />
          )}

          {/* FP3-052 / FP3-059 / FP3-069 — Assistant providers card.
              FP3-069 follow-up — hide entirely from assistants. Only
              the solo provider OWNER manages assistant invites. */}
          {assistantRequest && !isAssistant && (
            <AssistantProvidersCard
              assistantRequest={assistantRequest}
              isSoloOwner={isSoloOwner}
              onChanged={refresh}
              supervisedAssistants={supervisedAssistants}
            />
          )}

          {/* Org admin contacts */}
          <SectionCard
            testId="workspace-info-admins"
            icon={<ShieldCheck size={18} weight="duotone" className="text-[#1E3F33]" />}
            title="Organization admins"
            empty={admins.length === 0}
            emptyText={
              // FP3-051 — Friendlier copy for solo provider owners.
              // The seeded org has no admin user because the provider
              // *is* the owner; previous copy made it look like an
              // error state.
              isSoloOwner
                ? "You are the owner of this solo workspace. Platform support: simplyslps@gmail.com"
                : "No organization administrator listed yet."
            }
            wide
          >
            {admins.length > 0 && (
              <ul className="space-y-2.5">
                {admins.map((a) => (
                  <li
                    key={a.id}
                    data-testid={`workspace-admin-${a.id}`}
                    className="flex items-start justify-between gap-3 py-2 border-b border-[#F1F2F4] last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-[#1F2937] font-medium truncate">
                        {a.name || a.email}
                      </div>
                      <div className="text-[11.5px] text-[#6B7280] uppercase tracking-[0.08em]">
                        {ROLE_LABEL[a.role] || a.role || "—"}
                      </div>
                      <a
                        href={`mailto:${a.email}`}
                        data-testid={`workspace-admin-email-${a.id}`}
                        className="mt-1 inline-flex items-center gap-1 text-[12px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                      >
                        <EnvelopeSimple size={12} weight="duotone" />
                        {a.email}
                      </a>
                    </div>
                    <span
                      className={`flex-shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] ${statusTone(a.account_status || "active")}`}
                    >
                      {a.account_status || "active"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <div className="mt-7 flex items-center justify-end">
          <Button
            data-testid="workspace-info-account-security-link"
            onClick={() => navigate("/account/security")}
            variant="outline"
            className="h-9 px-3.5 text-[12.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            Account security & password →
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ icon, title, children, empty, emptyText, wide, testId }) {
  return (
    <Card
      data-testid={testId}
      className={`border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl ${wide ? "md:col-span-2" : ""}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-[14px] text-[#1F2937] heading-font font-semibold">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-[12.5px] text-[#6B7280] leading-relaxed">{emptyText}</p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold w-28 flex-shrink-0">
        {label}
      </dt>
      <dd
        className={`text-[13px] text-[#1F2937] min-w-0 flex-1 ${mono ? "font-mono text-[12.5px]" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}

// FP3-053 — Row variant used inside a `<dl className="divide-y …">`
// list. Layout is identical to `Row` but adds top/bottom padding so
// the dividers space out evenly. Used by the Assistant providers
// card (FP3-052). Defined here so the page never references an
// undefined component.
function InfoRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 py-2 first:pt-0 last:pb-0">
      <dt className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold w-32 flex-shrink-0">
        {label}
      </dt>
      <dd className="text-[13px] text-[#1F2937] min-w-0 flex-1 break-words">
        {value}
      </dd>
    </div>
  );
}

// FP3-069 — Assistant providers card with direct-invite UX. Solo
// owners can now send invites straight to their assistants (SLPA /
// COTA / PTA) instead of submitting a SuperAdmin-gated request. The
// card surfaces:
//   • Pending direct invites (with status + sent date)
//   • Active assistant count
//   • Any legacy in-flight FP3-059 access request (read-only with
//     a Cancel control so old pending rows never get stranded).
// The card stays read-only for non-solo viewers (assistants, admin
// shadows, etc.).
// FP3-091 — "Assistants you supervise" — canonical supervision
// relationship card. Relationship visibility is independent of
// account activation: rostered (no invite yet), invited, and active
// assistants all render, each with SEPARATE badges for account
// status, availability, and supervision status. Read-only; no
// student or caseload details.
const ACCOUNT_STATUS_META = {
  rostered: {
    label: "Rostered — invitation not sent",
    cls: "bg-[#F3F1EC] text-[#6B7280]",
  },
  invited: {
    label: "Invited — activation pending",
    cls: "bg-[#FBEDD4] text-[#7A5B1F]",
  },
  active: { label: "Active", cls: "bg-[#E8F0EB] text-[#1E3F33]" },
};

const PROVIDER_STATUS_LABEL = {
  paused: "Paused",
  on_leave: "On leave",
  unavailable: "Unavailable",
  inactive: "Inactive",
};

function SupervisedAssistantsCard({ assistants, selfProviderId }) {
  return (
    <SectionCard
      testId="workspace-supervised-assistants"
      icon={<UsersThree size={18} weight="duotone" className="text-[#1E3F33]" />}
      title="Assistants you supervise"
      wide
    >
      <ul className="divide-y divide-[#F1F2F4]">
        {assistants.map((a) => {
          const acct = ACCOUNT_STATUS_META[(a.account_status || "").toLowerCase()]
            || ACCOUNT_STATUS_META.rostered;
          const availability = (a.provider_status || "").toLowerCase();
          const availabilityLabel = PROVIDER_STATUS_LABEL[availability] || "";
          const supervisionInactive =
            (a.supervision_status || "").toLowerCase() === "inactive";
          const typeLabel = a.assistant_type || a.provider_type || "";
          const schoolNames = (a.schools || [])
            .map((s) => s.name)
            .filter(Boolean);
          const supervisorNames = (a.supervising_providers || [])
            .map((s) => s.name)
            .filter(Boolean);
          const sharedWith = (a.shared_with || [])
            .filter((s) => s.id !== selfProviderId && (s.name || "").trim());
          return (
            <li
              key={a.provider_id}
              data-testid={`supervised-assistant-${a.provider_id}`}
              className="py-2.5 first:pt-0 last:pb-0"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] text-[#1F2937] font-medium">
                      {a.name || a.email || "—"}
                    </span>
                    {typeLabel && (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-[0.08em] rounded-full px-1.5 py-0.5 bg-[#F3F1EC] text-[#3F4756] font-semibold">
                        {typeLabel}
                      </span>
                    )}
                  </div>
                  {schoolNames.length > 0 && (
                    <div
                      data-testid={`supervised-assistant-schools-${a.provider_id}`}
                      className="text-[11.5px] text-[#6B7280] mt-0.5"
                    >
                      {schoolNames.join(", ")}
                    </div>
                  )}
                  {supervisorNames.length > 0 && (
                    <div
                      data-testid={`supervised-assistant-supervisors-${a.provider_id}`}
                      className="text-[11.5px] text-[#6B7280] mt-0.5"
                    >
                      Supervisors: {supervisorNames.join(" · ")}
                    </div>
                  )}
                  {sharedWith.length > 0 && (
                    <div
                      data-testid={`supervised-assistant-shared-${a.provider_id}`}
                      className="text-[11px] text-[#3F4756] mt-0.5 italic"
                    >
                      Shared with {sharedWith.map((s) => s.name).join(" · ")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span
                    data-testid={`supervised-assistant-status-${a.provider_id}`}
                    className={`inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.06em] ${acct.cls}`}
                  >
                    {acct.label}
                  </span>
                  {availabilityLabel && (
                    <span
                      data-testid={`supervised-assistant-availability-${a.provider_id}`}
                      className="inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.06em] bg-[#FBEFE8] text-[#7A3A2C]"
                    >
                      {availabilityLabel}
                    </span>
                  )}
                  {supervisionInactive && (
                    <span
                      data-testid={`supervised-assistant-supervision-${a.provider_id}`}
                      className="inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.06em] bg-[#F3F1EC] text-[#9AA0A6]"
                    >
                      Supervision inactive
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-[10.5px] text-[#9AA0A6] mt-3 leading-relaxed">
        Supervision relationships appear as soon as an assistant is
        rostered and assigned to you — before their invitation is sent
        or their account is activated. The supervisor responsible for a
        specific student is set on that student&rsquo;s team assignment.
      </p>
    </SectionCard>
  );
}

function AssistantProvidersCard({ assistantRequest, isSoloOwner, onChanged, supervisedAssistants = [] }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [legacyRequestOpen, setLegacyRequestOpen] = useState(false);
  const cur = assistantRequest.current_request || null;
  const status = (cur?.status || "").toLowerCase();
  // FP3-091 — once a provider record exists, the supervised-assistants
  // relationship list is the source of truth; don't ALSO render that
  // person as a pending invite here.
  const knownEmails = new Set(
    supervisedAssistants.map((a) => (a.email || "").toLowerCase()).filter(Boolean)
  );
  const pendingInvites = (Array.isArray(assistantRequest.pending_invites)
    ? assistantRequest.pending_invites
    : []
  ).filter((inv) => !knownEmails.has((inv.email || "").toLowerCase()));
  const activeAssistants = Number(assistantRequest.active_assistants || 0);
  const maxAssistants = 2;
  const atCap = (activeAssistants + pendingInvites.length) >= maxAssistants;

  const submittedAt = cur?.submitted_at
    ? new Date(cur.submitted_at).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      })
    : "";

  const cancelLegacy = async () => {
    if (!cur?.id) return;
    if (!window.confirm(
      "Cancel this pending assistant access request? " +
      "It will no longer be reviewed by platform support."
    )) return;
    try {
      await api.cancelAssistantAccessRequest(cur.id);
      toast.success("Old assistant access request cancelled");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel the request");
    }
  };

  const renderLegacyStatusPill = () => {
    if (!cur) return null;
    if (status === "pending") {
      return (
        <span
          data-testid="assistant-request-status-pending"
          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#FBEDD4] text-[#7A5B1F]"
        >
          <Clock size={11} weight="duotone" /> Pending review
        </span>
      );
    }
    if (status === "approved") {
      return (
        <span
          data-testid="assistant-request-status-approved"
          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#E8F0EB] text-[#1E3F33]"
        >
          <CheckCircle size={11} weight="fill" /> Approved
        </span>
      );
    }
    if (status === "declined") {
      return (
        <span
          data-testid="assistant-request-status-declined"
          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#FBEFE8] text-[#7A3A2C]"
        >
          <XCircle size={11} weight="fill" /> Declined
        </span>
      );
    }
    if (status === "cancelled") {
      return (
        <span
          data-testid="assistant-request-status-cancelled"
          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#F3F1EC] text-[#6B7280]"
        >
          Cancelled
        </span>
      );
    }
    return null;
  };

  // Old pending request that pre-dates the FP3-069 direct-invite
  // flow. Surfaced read-only with a Cancel CTA so the user can
  // close it out manually.
  const hasOpenLegacyRequest = !!cur && status === "pending";

  return (
    <SectionCard
      testId="workspace-info-assistants"
      icon={<UsersThree size={18} weight="duotone" className="text-[#1E3F33]" />}
      title="Assistant providers"
      wide
    >
      <dl className="text-[13px] divide-y divide-[#F1F2F4]">
        <InfoRow
          label="Active assistants"
          value={String(activeAssistants)}
        />
        <InfoRow
          label="Pending invites"
          value={String(pendingInvites.length)}
        />
        <InfoRow
          label="Cap"
          value={`${activeAssistants + pendingInvites.length} of ${maxAssistants} used`}
        />
      </dl>

      {/* FP3-069 — Pending direct invites list. */}
      {pendingInvites.length > 0 && (
        <ul
          data-testid="assistant-pending-invites"
          className="mt-4 space-y-2 border-t border-[#F1F2F4] pt-3"
        >
          {pendingInvites.map((inv) => {
            const sent = inv.last_sent_at || inv.created_at;
            const sentLabel = sent
              ? new Date(sent).toLocaleDateString(undefined, {
                  month: "short", day: "numeric", year: "numeric",
                })
              : "";
            const fullName = [inv.first_name, inv.last_name]
              .filter(Boolean).join(" ").trim();
            return (
              <li
                key={inv.id}
                data-testid={`assistant-pending-invite-${inv.id}`}
                className="flex items-start justify-between gap-3 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-[#1F2937] font-medium truncate">
                    {fullName || inv.email}
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{inv.email}</span>
                    {inv.assistant_type && (
                      <span className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] rounded-full px-1.5 py-0.5 bg-[#F3F1EC] text-[#3F4756] font-semibold">
                        {inv.assistant_type}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                  <span
                    className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#FBEDD4] text-[#7A5B1F]"
                  >
                    <Clock size={11} weight="duotone" /> Pending
                  </span>
                  {sentLabel && (
                    <span className="text-[10.5px] text-[#9AA0A6]">
                      Sent {sentLabel}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* FP3-069 — Direct invite CTA (solo owner only). */}
      {isSoloOwner && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={() => setInviteOpen(true)}
            data-testid="invite-assistant-open-modal"
            disabled={atCap}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white disabled:bg-[#1E3F33]/40"
          >
            <Plus size={13} weight="bold" className="mr-1" />
            Invite assistant
          </Button>
          {atCap && (
            <span
              data-testid="invite-assistant-cap-note"
              className="text-[11px] text-[#7A5B1F] bg-[#FBEDD4] rounded-full px-2 py-0.5"
            >
              At cap ({maxAssistants} of {maxAssistants}) — revoke or accept an
              invite before sending another.
            </span>
          )}
        </div>
      )}

      <p className="text-[10.5px] text-[#9AA0A6] mt-3 leading-relaxed">
        Inviting an assistant does <strong>not</strong> start
        billing and does <strong>not</strong> require platform
        approval. The assistant accepts via email, sets a password,
        and is automatically scoped under your supervision. They
        start with zero students assigned — you control caseload
        sharing.
      </p>

      {/* FP3-069 — Legacy access-request banner. Only renders when
          the solo owner has an in-flight pre-FP3-069 request that's
          still pending. Read-only with a clear Cancel CTA so old
          rows never get stranded. */}
      {hasOpenLegacyRequest && (
        <div
          data-testid="legacy-assistant-request-banner"
          className="mt-4 rounded-md border border-[#E8D6CD] bg-[#FBEFE8] p-3 text-[12px]"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#7A3A2C]">
                  Legacy request
                </span>
                {renderLegacyStatusPill()}
              </div>
              <p className="text-[12px] text-[#4B5563] leading-relaxed">
                You submitted an assistant access request{submittedAt
                  ? ` on ${submittedAt}` : ""} before we replaced that
                flow with direct invites. The request is still in
                review with platform support. You can cancel it and
                use <strong>Invite assistant</strong> above instead.
              </p>
              {(cur?.note || "").trim() && (
                <p className="text-[11px] text-[#6B7280] mt-1">
                  Your note: <em>{cur.note}</em>
                </p>
              )}
            </div>
            {isSoloOwner && (
              <Button
                type="button"
                variant="outline"
                onClick={cancelLegacy}
                data-testid="legacy-assistant-request-cancel"
                className="h-7 text-[11.5px] border-[#E5E3DB] text-[#3F4756]"
              >
                Cancel old request
              </Button>
            )}
          </div>
        </div>
      )}

      {/* FP3-069 — Direct invite modal (primary path). */}
      <InviteAssistantModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => onChanged?.()}
      />
      {/* Legacy modal retained behind a hidden trigger so any in-app
          deep link continues to work; not surfaced in the UI. */}
      <AssistantAccessRequestModal
        open={legacyRequestOpen}
        onOpenChange={setLegacyRequestOpen}
        onSubmitted={() => onChanged?.()}
      />
    </SectionCard>
  );
}
